import { LitElement, html, css } from 'lit';
import { classMap } from 'lit/directives/class-map.js';
import { styleMap } from 'lit/directives/style-map.js';
import { customElement, property, query, state } from 'lit/decorators.js';
import { dispatch } from '../../utils/event-helpers';
import { generateUid } from '../../utils/uid';

type ChevronPosition = 'left' | 'right';
export type HeadingLevel = 1 | 2 | 3 | 4 | 5 | 6;
export type PanelType = 'container' | 'line';



/**
 * A single collapsible disclosure panel consisting of a persistent header and
 * an expandable body.
 *
 * The header is always visible and acts as a toggle trigger. The body reveals
 * with a smooth CSS `grid-template-rows` animation when the panel is opened.
 * Use `asc-accordion-panel` as a standalone disclosure unit or compose multiple
 * panels inside a parent group to build a full accordion.
 *
 * Open/close state is tracked internally and can be controlled externally via
 * the `open` attribute. The component is presentational — logic stays outside.
 *
 * @element asc-accordion-panel
 *
 * @fires asc-toggle - Fired after `open` changes from user interaction (not programmatic). Detail: `{ open: boolean }`
 *
 * @slot header - Required. The panel's heading text, always visible inside the trigger button. Use phrasing content only — plain text, `<span>`, `<strong>`, `<em>`. Block-level elements are invalid inside a `<button>`.
 * @slot icon - Optional leading icon displayed inside the trigger, to the left of the header text. Accepts any `<svg>` element. Hidden automatically when empty.
 * @slot indicator-closed - Optional custom indicator shown when the panel is **closed**. If only this slot is filled, the element rotates 180° when the panel opens (same behaviour as the default chevron). If both `indicator-closed` and `indicator-open` are filled, the two icons swap on toggle instead.
 * @slot indicator-open - Optional custom indicator shown when the panel is **open**. Only meaningful when `indicator-closed` is also filled — the component swaps between the two icons on toggle.
 * @slot - Default slot. Body content revealed when the panel is open. Accepts any HTML. Always indexable — slotted content lives in light DOM.
 *
 * @csspart base - Root `<div>` — top-level layout container with border and shadow.
 * @csspart heading - `<div role="heading">` — the semantic heading wrapper.
 * @csspart trigger - `<button>` — the interactive toggle control; primary header styling target.
 * @csspart icon - `<span>` wrapping the `icon` slot; hidden when slot is empty.
 * @csspart label - `<span>` wrapping the `header` slot; grows to fill remaining trigger width.
 * @csspart indicator - `<span>` wrapping the indicator slots and their fallback chevron; owns flex order and sizing.
 * @csspart chevron - Animated `<svg>` chevron; shown only in the default mode (neither `indicator-closed` nor `indicator-open` is filled). Rotates 180° when open.
 * @csspart panel - Collapsible `<div>` — the outer grid-row animation container.
 * @csspart body - Inner `<div>` — wraps the default slot; provides overflow clipping and padding.
 *
 * @cssprop --asc-accordion-panel-bg - Panel background color. Default: `var(--asc-bg-primary)`
 * @cssprop --asc-accordion-panel-header-bg - Header trigger background. Default: `var(--asc-bg-primary)`
 * @cssprop --asc-accordion-panel-header-bg-hover - Header background on hover. Default: `var(--asc-bg-primary_hover)`
 * @cssprop --asc-accordion-panel-border-color - Border color. Default: `var(--asc-border-tertiary)`
 * @cssprop --asc-accordion-panel-border-color-hover - Border color on hover. Default: `var(--asc-border-secondary)`
 * @cssprop --asc-accordion-panel-border-radius - Border radius (container type only). Default: `var(--asc-radius-sm)`
 * @cssprop --asc-accordion-panel-padding-x - Horizontal padding. Default: `var(--asc-spacing-xl)`
 * @cssprop --asc-accordion-panel-padding-y - Vertical padding. Default: `var(--asc-spacing-lg)`
 * @cssprop --asc-accordion-panel-text-color - Header text color. Default: `var(--asc-text-primary)`
 * @cssprop --asc-accordion-panel-chevron-color - Chevron icon color. Default: `var(--asc-fg-secondary)`
 * @cssprop --asc-accordion-panel-transition-duration - Animation transition duration. Default: `200ms`
 * @cssprop --asc-accordion-panel-chevron-gap - Gap between the toggle indicator and adjacent content. Default: `var(--asc-spacing-md)`
 */
@customElement('asc-accordion-panel')
export class AscAccordionPanel extends LitElement {
  static override styles = css`
    :host {
      display: block;
    }

    /* ── Base wrapper ─────────────────────────────────────────── */
    .base {
      background-color: var(--asc-accordion-panel-bg, var(--asc-bg-primary));
      border: var(--asc-border-width) solid
        var(--asc-accordion-panel-border-color, var(--asc-border-tertiary));
      border-radius: var(--asc-accordion-panel-border-radius, var(--asc-radius-sm));
      box-shadow: 0px 1px 2px 0px var(--asc-shadow-xs, rgba(10, 13, 18, 0.05));
      overflow: hidden;
    }

    /* Container: border color shifts on hover */
    :host(:hover:not([disabled])) .base {
      border-color: var(--asc-accordion-panel-border-color-hover, var(--asc-border-secondary));
    }

    /* ── Line type ────────────────────────────────────────────── */
    :host([type='line']) .base {
      border: none;
      border-bottom: var(--asc-border-width) solid
        var(--asc-accordion-panel-border-color, var(--asc-border-tertiary));
      border-radius: 0;
      box-shadow: none;
    }

    :host([type='line']:hover:not([disabled])) .base {
      border-bottom-color: var(
        --asc-accordion-panel-border-color-hover,
        var(--asc-border-secondary)
      );
    }

    /* ── Trigger button ───────────────────────────────────────── */
    .trigger {
      display: flex;
      align-items: center;
      width: 100%;
      gap: var(--asc-accordion-panel-chevron-gap, var(--asc-spacing-md));
      padding:
        var(--asc-accordion-panel-padding-y, var(--asc-spacing-lg))
        var(--asc-accordion-panel-padding-x, var(--asc-spacing-xl));
      background-color: var(--asc-accordion-panel-header-bg, var(--asc-bg-primary));
      border: none;
      cursor: pointer;
      color: var(--asc-accordion-panel-text-color, var(--asc-text-primary));
      font-family: var(--asc-font-family-body);
      font-size: var(--asc-font-size-text-md);
      font-weight: var(--asc-font-weight-medium);
      line-height: var(--asc-line-height-text-md);
      text-align: left;
      transition:
        background-color
        var(--asc-accordion-panel-transition-duration, var(--asc-transition-base));
    }

    .trigger:hover:not(:disabled) {
      background-color: var(
        --asc-accordion-panel-header-bg-hover,
        var(--asc-bg-primary_hover)
      );
    }

    .trigger:focus-visible {
      outline: 2px solid var(--asc-focus-ring);
      outline-offset: -2px;
    }

    :host([disabled]) .trigger {
      cursor: not-allowed;
      opacity: 0.5;
    }

    /* ── Icon slot ────────────────────────────────────────────── */
    .icon {
      display: inline-flex;
      align-items: center;
      flex-shrink: 0;
      width: 20px;
      height: 20px;
      overflow: hidden;
    }

    .icon[hidden] {
      display: none;
    }

    /* ── Label ────────────────────────────────────────────────── */
    .label {
      flex: 1 0 0;
      min-width: 0;
      text-align: left;
    }

    /* ── Indicator (toggle icon wrapper) ─────────────────────── */
    .indicator {
      flex-shrink: 0;
      width: 20px;
      height: 20px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      order: 3;
    }

    /* Indicator moves before icon and label when position is 'left' */
    :host([chevron-position='left']) .indicator {
      order: -1;
    }

    /* ── Indicator sub-wrappers ───────────────────────────────── */
    .indicator-closed-wrap,
    .indicator-open-wrap {
      display: none;
      width: 100%;
      height: 100%;
      align-items: center;
      justify-content: center;
    }

    /* Mode: single (only indicator-closed filled) — show it, rotate on open */
    .indicator--single .indicator-closed-wrap {
      display: inline-flex;
      transition:
        transform
        var(--asc-accordion-panel-transition-duration, var(--asc-transition-base));
    }

    :host([open]) .indicator--single .indicator-closed-wrap {
      transform: rotate(180deg);
    }

    /* Mode: both (indicator-closed + indicator-open filled) — swap on open */
    .indicator--both .indicator-closed-wrap {
      display: inline-flex;
    }

    :host([open]) .indicator--both .indicator-closed-wrap {
      display: none;
    }

    :host([open]) .indicator--both .indicator-open-wrap {
      display: inline-flex;
    }

    /* Hide fallback chevron when any custom indicator slot is in use */
    .indicator--single .chevron,
    .indicator--both .chevron {
      display: none;
    }

    /* ── Chevron (default indicator fallback) ─────────────────── */
    .chevron {
      width: 100%;
      height: 100%;
      color: var(--asc-accordion-panel-chevron-color, var(--asc-fg-secondary));
      transition:
        transform
        var(--asc-accordion-panel-transition-duration, var(--asc-transition-base));
      transform: rotate(0deg);
    }

    :host([open]) .chevron {
      transform: rotate(180deg);
    }

    /* ── Panel (collapsible region) ───────────────────────────── */
    .panel {
      display: grid;
      grid-template-rows: 0fr;
      transition:
        grid-template-rows
        var(--asc-accordion-panel-transition-duration, var(--asc-transition-base));
    }

    :host([open]) .panel {
      grid-template-rows: 1fr;
    }

    /* ── Body ─────────────────────────────────────────────────── */
    .body {
      overflow: hidden;    }

    /* Inner wrapper carries the padding so .body can fully collapse to 0.
       Left padding is set dynamically via styleMap to align body content
       under the label slot, respecting icon presence and chevron position. */
    .body-inner {
      padding:
        var(--asc-accordion-panel-padding-y, var(--asc-spacing-lg))
        var(--asc-accordion-panel-padding-x, var(--asc-spacing-xl));
    }
  `;

  /** Toggles panel body visibility. Drives `:host([open])` CSS selectors for chevron rotation and panel reveal. */
  @property({ type: Boolean, reflect: true }) open = false;

  /** Prevents user interaction. Sets `disabled` on the `<button>` and `aria-disabled="true"`. Open state cannot be changed by the user when disabled. */
  @property({ type: Boolean, reflect: true }) disabled = false;

  /**
   * Controls whether the chevron indicator appears to the left or right of the header and icon.
   * Reflected so `:host([chevron-position="left"])` CSS selectors can reorder flex children.
   * DOM order never changes — only the CSS `order` property is toggled.
   */
  @property({ type: String, reflect: true, attribute: 'chevron-position' })
  chevronPosition: ChevronPosition = 'right';

  /**
   * Controls the `aria-level` of the heading wrapper in Shadow DOM.
   * Set to match your document heading outline. Not reflected — purely semantic, does not affect styling.
   */
  @property({ type: Number, attribute: 'heading-level' })
  headingLevel: HeadingLevel = 3;

  /**
   * Controls the visual framing of the panel.
   * `container` (default) renders a full border, border-radius, and drop shadow.
   * `line` renders a bottom border only with no radius or shadow — for flush list-style accordions.
   * Reflected so `:host([type="line"])` CSS selectors control styling.
   */
  @property({ type: String, reflect: true })
  type: PanelType = 'container';

  @state() private _hasIcon = false;
  @state() private _hasIndicatorClosed = false;
  @state() private _hasIndicatorOpen = false;

  @query('.trigger') private _trigger!: HTMLButtonElement;

  /** Unique ID for this instance, used to scope trigger/panel ARIA relationships within this shadow root. */
  private readonly _uid = generateUid();

  /**
   * Moves focus to the trigger button inside this panel's shadow root.
   * Called by `asc-accordion-group` during arrow-key navigation to shift
   * keyboard focus across the shadow DOM boundary.
   */
  focusTrigger(): void {
    this._trigger?.focus();
  }

  /**
   * Computes the left padding for the body-inner wrapper so that body content
   * aligns under the label slot regardless of icon presence or chevron position.
   * Each leading element (chevron-left, visible icon) adds its width + the gap.
   */
  private get _bodyPaddingLeft(): string {
    const px = 'var(--asc-accordion-panel-padding-x, var(--asc-spacing-xl))';
    const gap = 'var(--asc-accordion-panel-chevron-gap, var(--asc-spacing-md))';
    const icon = '20px';
    let offset = 0;
    if (this.chevronPosition === 'left') offset++;
    if (this._hasIcon) offset++;
    if (offset === 0) return px;
    if (offset === 1) return `calc(${px} + ${icon} + ${gap})`;
    return `calc(${px} + ${icon} + ${gap} + ${icon} + ${gap})`;
  }

  private _handleIconSlotChange(e: Event): void {
    const slot = e.target as HTMLSlotElement;
    this._hasIcon = slot.assignedNodes({ flatten: true }).length > 0;
  }

  private _handleIndicatorClosedSlotChange(e: Event): void {
    const slot = e.target as HTMLSlotElement;
    this._hasIndicatorClosed = slot.assignedNodes({ flatten: true }).length > 0;
  }

  private _handleIndicatorOpenSlotChange(e: Event): void {
    const slot = e.target as HTMLSlotElement;
    this._hasIndicatorOpen = slot.assignedNodes({ flatten: true }).length > 0;
  }

  private _handleToggle(): void {
    if (this.disabled) return;
    this.open = !this.open;
    dispatch<{ open: boolean }>(this, 'asc-toggle', { detail: { open: this.open } });
  }

  override render() {
    return html`
      <div part="base" class="base">
        <div
          role="heading"
          aria-level="${this.headingLevel}"
          part="heading"
          class="heading"
        >
          <button
            part="trigger"
            class="trigger"
            type="button"
            aria-expanded=${this.open ? 'true' : 'false'}
            aria-controls=${`${this._uid}-panel`}
            aria-disabled=${this.disabled ? 'true' : 'false'}
            ?disabled=${this.disabled}
            id=${`${this._uid}-trigger`}
            @click=${this._handleToggle}
          >
            <span part="icon" class="icon" ?hidden=${!this._hasIcon}>
              <slot name="icon" @slotchange=${this._handleIconSlotChange}></slot>
            </span>
            <span part="label" class="label">
              <slot name="header"></slot>
            </span>
            <span
              part="indicator"
              class=${classMap({
                indicator: true,
                'indicator--both': this._hasIndicatorClosed && this._hasIndicatorOpen,
                'indicator--single': this._hasIndicatorClosed && !this._hasIndicatorOpen,
                'indicator--default': !this._hasIndicatorClosed,
              })}
            >
              <span class="indicator-closed-wrap">
                <slot
                  name="indicator-closed"
                  @slotchange=${this._handleIndicatorClosedSlotChange}
                ></slot>
              </span>
              <span class="indicator-open-wrap">
                <slot
                  name="indicator-open"
                  @slotchange=${this._handleIndicatorOpenSlotChange}
                ></slot>
              </span>
              <svg
                part="chevron"
                class="chevron"
                aria-hidden="true"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M5 7.5L10 12.5L15 7.5"
                  stroke="currentColor"
                  stroke-width="1.67"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </span>
          </button>
        </div>
        <div
          part="panel"
          class="panel"
          role="region"
          aria-labelledby=${`${this._uid}-trigger`}
          id=${`${this._uid}-panel`}
          ?inert=${!this.open}
        >
          <div part="body" class="body">
            <div class="body-inner" style=${styleMap({ 'padding-left': this._bodyPaddingLeft })}>
              <slot></slot>
            </div>
          </div>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'asc-accordion-panel': AscAccordionPanel;
  }
}
